import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { DateRangePicker } from '@/components/forms/DateRangePicker';
import { Tabs } from '@/components/common/Tabs';
import { formatNumber, pnlClass, formatDateTime } from '@/utils/format';
import { loginOverviewApi } from '@/api/services/loginOverview';
import type {
  HistoryTableRecord,
  NetOpenPositionDto,
  OverViewTableRecord,
  OverviewSummaryDto,
} from '@/api/types/loginOverview';
import { LoginSearch } from './LoginSearch';
import { useLoginOverviewLive } from './useLoginOverviewLive';

type Tab = 'nop' | 'overview' | 'history';

const fmt = (v: number | null | undefined) => (v == null ? '0.00' : formatNumber(v));
const pct = (v: number | null | undefined) => `${v == null ? '0.00' : formatNumber(v, 2)}%`;
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

interface Kpi {
  label: string;
  value: string;
  icon: string;
  gradient: string;
  pnl?: number | null;
}

// Gradient-icon KPI strip (legacy _LoginKpiCards / _HistorySummary). When `compact`
// (modal minimized) it's forced to 2-per-row with no avatar and smaller text; the
// avatar also hides on xs screens.
function KpiStrip({ cards, compact }: { cards: Kpi[]; compact?: boolean }) {
  return (
    <div className={`row g-2 mb-3 row-cols-2${compact ? '' : ' row-cols-md-4'}`}>
      {cards.map((c) => (
        <div className="col" key={c.label}>
          <div className="card custom-card border mb-0 h-100">
            <div className="card-body d-flex align-items-center gap-2">
              <span className={`avatar avatar-sm rounded-3 ${c.gradient} d-inline-flex align-items-center justify-content-center`}>
                <i className={`${c.icon} fs-14 text-white`} aria-hidden="true" />
              </span>
              <div className="overflow-hidden">
                <p className={`mb-0 text-muted text-truncate ${compact ? 'fs-11' : 'fs-13'}`}>{c.label}</p>
                <h5 className={`mb-0 fw-semibold ${compact ? 'fs-14' : ''} ${c.pnl !== undefined ? pnlClass(c.pnl) : ''}`}>{c.value}</h5>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const DEAL_TYPES = [
  { key: 'trade', label: 'Trade' },
  { key: 'depositWithdrawals', label: 'Deposits/Withdrawals' },
  { key: 'credit', label: 'Credit' },
  { key: 'charges', label: 'Charges' },
  { key: 'dividend', label: 'Dividend' },
  { key: 'dealAgent', label: 'Deal Agent' },
  { key: 'dealSoCompensation', label: 'Deal SO Compensation' },
] as const;

type DealKey = (typeof DEAL_TYPES)[number]['key'];

interface HistState {
  begDate: string | null;
  endDate: string | null;
  allHistory: boolean;
  trade: boolean;
  depositWithdrawals: boolean;
  credit: boolean;
  charges: boolean;
  dividend: boolean;
  dealAgent: boolean;
  dealSoCompensation: boolean;
}

// Default: All History checked, with the date range pre-filled to today (shown in the
// picker; it applies once the user picks a range, which flips All History off). The tab
// fetches on open.
function makeDefaultHist(): HistState {
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return {
    begDate: today,
    endDate: today,
    allHistory: true,
    trade: false,
    depositWithdrawals: false,
    credit: false,
    charges: false,
    dividend: false,
    dealAgent: false,
    dealSoCompensation: false,
  };
}

export function LoginOverviewModal({
  login,
  connector,
  onClose,
}: {
  login: number;
  connector: number;
  onClose: () => void;
}) {
  const [cur, setCur] = useState({ login, connector });
  const [toRc, setToRc] = useState(false);
  const [tab, setTab] = useState<Tab>('nop');
  const [minimized, setMinimized] = useState(false);
  const [dragPos, setDragPos] = useState<{ top: number; left: number } | null>(null);

  const lc = useMemo(() => ({ login: cur.login, connector: cur.connector, toRcConverted: toRc }), [cur, toRc]);

  // Single source of truth: any change (date range or checkbox) updates histDraft and
  // the history query is keyed on it, so it refetches directly — no separate "apply" step.
  const [histDraft, setHistDraft] = useState<HistState>(makeDefaultHist);

  const summaryQ = useQuery({ queryKey: ['lo-summary', lc], queryFn: () => loginOverviewApi.getOverViewSummary(lc) });
  const nopQ = useQuery({ queryKey: ['lo-nop', lc], queryFn: () => loginOverviewApi.getNop(lc) });
  const ovQ = useQuery({ queryKey: ['lo-ov', lc], queryFn: () => loginOverviewApi.getOverView({ ...lc, maxResultCount: 2000 }) });
  const histQ = useQuery({
    queryKey: ['lo-hist', cur, histDraft],
    queryFn: () => loginOverviewApi.getHistory({ login: cur.login, connector: cur.connector, ...histDraft, maxResultCount: 2000 }),
  });

  const [liveSummary, setLiveSummary] = useState<OverviewSummaryDto | null>(null);
  const [liveOv, setLiveOv] = useState<OverViewTableRecord[] | null>(null);
  const [liveOvTotals, setLiveOvTotals] = useState<{ p: number; s: number; c: number } | null>(null);
  const [liveNop, setLiveNop] = useState<NetOpenPositionDto[] | null>(null);
  const [liveNopTotals, setLiveNopTotals] = useState<{ fl: number; exp: number; cur: string | null } | null>(null);

  useEffect(() => {
    setLiveSummary(null);
    setLiveOv(null);
    setLiveOvTotals(null);
    setLiveNop(null);
    setLiveNopTotals(null);
  }, [lc]);

  useLoginOverviewLive({
    enabled: true,
    login: cur.login,
    connector: cur.connector,
    toRc,
    onOverview: (p) => {
      setLiveSummary(p.summary);
      setLiveOv(p.data.items);
      setLiveOvTotals({ p: p.data.totalProfit, s: p.data.totalSwap, c: p.data.totalCommission });
    },
    onNop: (p) => {
      setLiveNop(p.data.items);
      setLiveNopTotals({ fl: p.data.totalFloatingPnl, exp: p.data.totalExposure, cur: p.data.currency ?? null });
    },
  });

  const summary = liveSummary ?? summaryQ.data;
  const nopRows = liveNop ?? nopQ.data?.items ?? [];
  const nopTotals = liveNopTotals ?? (nopQ.data ? { fl: nopQ.data.totalFloatingPnl, exp: nopQ.data.totalExposure, cur: nopQ.data.currency ?? null } : null);
  const nopCurrency = nopTotals?.cur ?? null;
  const nopMissingGroups = (liveNop ? null : nopQ.data?.missingGroups) || null;
  const nopMissingSymbols = (liveNop ? null : nopQ.data?.missingSymbols) || null;
  const nopError = (liveNop ? null : nopQ.data?.errorMessage) || null;
  const ovRows = liveOv ?? ovQ.data?.items ?? [];
  const ovTotals = liveOvTotals ?? (ovQ.data ? { p: ovQ.data.totalProfit, s: ovQ.data.totalSwap, c: ovQ.data.totalCommission } : null);
  const histRows = histQ.data?.items ?? [];
  const histSummary = histQ.data?.summary;

  // Overview/NOP KPI strip: credit/margin/free-margin/margin-level from summary; commission/swap/profit from overview totals.
  const tradingKpis: Kpi[] = [
    { label: 'Credit', value: fmt(summary?.credit), icon: 'fa fa-credit-card', gradient: 'bg-success-gradient' },
    { label: 'Margin', value: fmt(summary?.margin), icon: 'fa fa-credit-card', gradient: 'bg-dark-gradient' },
    { label: 'Free Margin', value: fmt(summary?.freeMargin), icon: 'fa fa-credit-card', gradient: 'bg-danger-gradient' },
    { label: 'Margin Level', value: pct(summary?.marginLevel), icon: 'fa fa-tachometer', gradient: 'bg-primary-gradient' },
    { label: 'Commission', value: fmt(ovTotals?.c), icon: 'fa fa-percent', gradient: 'bg-teal-gradient' },
    { label: 'Swap', value: fmt(ovTotals?.s), icon: 'fa fa-exchange', gradient: 'bg-info' },
    { label: 'Profit', value: fmt(ovTotals?.p), icon: 'fa fa-bar-chart', gradient: 'bg-indigo', pnl: ovTotals?.p },
  ];
  const historyKpis: Kpi[] = [
    { label: 'Total Profit', value: fmt(histSummary?.profit), icon: 'fa fa-bar-chart', gradient: 'bg-primary-gradient', pnl: histSummary?.profit },
    { label: 'Deposit', value: fmt(histSummary?.deposit), icon: 'fa fa-university', gradient: 'bg-success-gradient' },
    { label: 'Withdrawal', value: fmt(histSummary?.withdrawal), icon: 'fa fa-money', gradient: 'bg-danger-gradient' },
    { label: 'Credit', value: fmt(histSummary?.credit), icon: 'fa fa-credit-card', gradient: 'bg-primary-gradient' },
    { label: 'Commission', value: fmt(histSummary?.tradeCommission), icon: 'fa fa-percent', gradient: 'bg-teal-gradient' },
    { label: 'Swap', value: fmt(histSummary?.tradeSwap), icon: 'fa fa-exchange', gradient: 'bg-info' },
    { label: 'Trade Profit', value: fmt(histSummary?.tradeProfit), icon: 'fa fa-bar-chart', gradient: 'bg-purple-gradient', pnl: histSummary?.tradeProfit },
  ];

  // History checkboxes: checking a type clears All History; checking All History clears types;
  // unchecking the last type re-checks All History. Each change refetches (legacy reloadHistory).
  const toggleDeal = useCallback((key: DealKey, checked: boolean) => {
    setHistDraft((d) => {
      const next: HistState = { ...d, [key]: checked };
      if (checked) {
        next.allHistory = false;
      } else if (!DEAL_TYPES.some((t) => next[t.key])) {
        next.allHistory = true;
      }
      return next;
    });
  }, []);
  const toggleAll = useCallback((checked: boolean) => {
    setHistDraft((d) => {
      const next: HistState = { ...d };
      if (checked) {
        next.allHistory = true;
        DEAL_TYPES.forEach((t) => (next[t.key] = false));
      } else {
        next.allHistory = false;
      }
      return next;
    });
  }, []);
  const fetchHistory = () => void histQ.refetch();

  // Drag (only while minimized) — mirrors FinkitModalManager._enableDrag.
  const dialogRef = useRef<HTMLDivElement>(null);
  const onHeaderMouseDown = (e: React.MouseEvent) => {
    if (!minimized) return;
    if ((e.target as HTMLElement).closest('button, a, input, select, label, .form-check, .select2-container')) return;
    const el = dialogRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    const onMove = (ev: MouseEvent) => {
      const maxLeft = Math.max(0, window.innerWidth - rect.width);
      const maxTop = Math.max(0, window.innerHeight - rect.height);
      setDragPos({ left: Math.min(Math.max(0, ev.clientX - offsetX), maxLeft), top: Math.min(Math.max(0, ev.clientY - offsetY), maxTop) });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    e.preventDefault();
  };
  const toggleMinimize = () => {
    setMinimized((m) => {
      if (m) setDragPos(null);
      return !m;
    });
  };

  const isHistory = tab === 'history';
  const dialogStyle: React.CSSProperties | undefined = minimized
    ? {
        position: 'fixed',
        width: 460,
        maxWidth: '90vw',
        margin: 0,
        zIndex: 1061,
        ...(dragPos
          ? { top: dragPos.top, left: dragPos.left, right: 'auto', bottom: 'auto' }
          : { right: '1rem', bottom: '1rem', top: 'auto', left: 'auto' }),
      }
    : undefined;

  return createPortal(
    <>
      {!minimized && <div className="modal-backdrop fade show" style={{ zIndex: 1200 }} />}
      <div className={`modal fade show d-block finkit-minimizable${minimized ? ' finkit-modal-minimized' : ''}`} tabIndex={-1} role="dialog" style={minimized ? { pointerEvents: 'none', zIndex: 1201 } : { zIndex: 1201 }}>
        <div
          ref={dialogRef}
          className={minimized ? 'modal-dialog' : 'modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable'}
          style={{ ...dialogStyle, pointerEvents: 'auto' }}
        >
          <div className="modal-content" style={minimized ? { maxHeight: '80vh' } : undefined}>
            <div
              className="modal-header align-items-start"
              style={minimized ? { cursor: 'move', borderBottom: 0, userSelect: 'none' } : undefined}
              onMouseDown={onHeaderMouseDown}
            >
              <LoginHeader
                summary={summary}
                login={cur.login}
                connector={cur.connector}
                minimized={minimized}
                toRc={toRc}
                isHistory={isHistory}
                balanceChange={histSummary?.balance}
                onToggleRc={setToRc}
                onPickLogin={(l, c) => setCur({ login: l, connector: c })}
              />
              <div className="d-flex gap-2 align-items-center">
                <button type="button" className="btn p-0 border-0 bg-transparent lh-1" title={minimized ? 'Restore' : 'Minimize'} onClick={toggleMinimize} style={{ opacity: 0.5 }}>
                  <i className={`fa ${minimized ? 'fa-window-restore' : 'fa-window-minimize'}`} />
                </button>
                <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
              </div>
            </div>

            <div className="modal-body text-muted pt-0" style={minimized ? { overflowY: 'auto' } : undefined}>
              <div className="position-sticky top-0 bg-white pt-0 pb-4" style={{ zIndex: 3 }}>
                <Tabs
                  variant="underline"
                  tabs={[
                    { key: 'nop', title: 'NOP' },
                    { key: 'overview', title: 'Overview' },
                    { key: 'history', title: 'History' },
                  ]}
                  active={tab}
                  onChange={(k) => setTab(k as Tab)}
                />
              </div>

              {tab === 'nop' && (
                <>
                  <KpiStrip cards={tradingKpis} compact={minimized} />
                  {nopCurrency && <div className="small text-muted mb-1">Values in {nopCurrency}</div>}
                  {nopError && <div className="alert alert-danger py-2">{nopError}</div>}
                  {nopMissingGroups && <div className="text-warning small" dangerouslySetInnerHTML={{ __html: nopMissingGroups }} />}
                  {nopMissingSymbols && <div className="text-warning small" dangerouslySetInnerHTML={{ __html: nopMissingSymbols }} />}
                  <DataGrid
                    columns={NOP_COLUMNS}
                    data={nopRows}
                    rowKey={(r) => `${r.symbol}-${r.connectorId}`}
                    includeExcel
                    excelFileName="login-overview-nop"
                    emptyMessage="No open positions."
                    footerTop={
                      <tr>
                        <td>Total</td>
                        <td className="text-end">{fmt(nopTotals?.exp)}</td>
                        <td>-</td>
                        <td className="text-end">{fmt(nopTotals?.fl)}</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                      </tr>
                    }
                  />
                </>
              )}

              {tab === 'overview' && (
                <>
                  <KpiStrip cards={tradingKpis} compact={minimized} />
                  <DataGrid columns={OV_COLUMNS} data={ovRows} rowKey={(r) => String(r.ticket)} includeExcel excelFileName="login-overview" emptyMessage="No open trades." />
                </>
              )}

              {tab === 'history' && (
                <>
                  <HistoryFilters draft={histDraft} setDraft={setHistDraft} onFetch={fetchHistory} onToggleAll={toggleAll} onToggleDeal={toggleDeal} loading={histQ.isFetching} />
                  <KpiStrip cards={historyKpis} compact={minimized} />
                  <DataGrid columns={HIST_COLUMNS} data={histRows} rowKey={(r) => r.deal} includeExcel excelFileName="login-history" emptyMessage="No history." />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

function LoginHeader({
  summary,
  login,
  connector,
  minimized,
  toRc,
  isHistory,
  balanceChange,
  onToggleRc,
  onPickLogin,
}: {
  summary: OverviewSummaryDto | undefined;
  login: number;
  connector: number;
  minimized: boolean;
  toRc: boolean;
  isHistory: boolean;
  balanceChange?: number;
  onToggleRc: (v: boolean) => void;
  onPickLogin: (login: number, connector: number) => void;
}) {
  return (
    <div className="flex-grow-1 me-3">
      <div className="row d-flex align-items-center g-2">
        <div className="col-12 col-lg-7">
          <div className="d-flex align-items-start flex-wrap">
            <span className={`avatar avatar-xxl avatar-rounded me-3 bg-light align-items-center justify-content-center ${minimized ? 'd-none' : 'd-none d-lg-inline-flex'}`}>
              <i className="fa fa-user fs-25 text-muted" />
            </span>
            <div className="flex-grow-1">
              <h4 className="mb-1"><span className="fw-semibold">{summary?.loginName || login}</span></h4>
              <div className="d-flex flex-wrap gap-2 mb-1 fs-13 text-muted">
                <span><i className="fa fa-hashtag me-1" />{summary?.uniqueId || '—'}</span>
                <span className="text-muted">|</span>
                <span><i className="fa fa-globe me-1" />{summary?.country || '—'}</span>
                <span className="text-muted">|</span>
                <span><i className="fa fa-id-badge me-1" />{summary?.mifidClassification || '—'}</span>
                <span className="text-muted">|</span>
                <span><i className="fa fa-money me-1" />{summary?.currency || '—'}</span>
              </div>
              <p className="text-muted mb-2 fs-13"><i className="fa fa-calendar me-1" />Member Since: {fmtDate(summary?.loginRegistrationDate)}</p>
              <div className="d-flex align-items-center gap-2">
                <div style={{ minWidth: 220 }}>
                  <LoginSearch value={`${login}+${connector}`} valueLabel={summary?.loginName || String(login)} onSelect={(l, c) => onPickLogin(l, c)} />
                </div>
                <div className="form-check form-switch ms-2 mb-0">
                  <input className="form-check-input" type="checkbox" id="lo-rc" checked={toRc} onChange={(e) => onToggleRc(e.target.checked)} />
                  <label className="form-check-label" htmlFor="lo-rc">RC</label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-lg-5">
          <div className="d-flex flex-wrap justify-content-lg-end gap-3">
            {isHistory ? (
              <HeaderStat label="Balance Change" value={balanceChange} icon="ri-exchange-dollar-line" gradient="bg-dark-gradient" />
            ) : (
              <>
                <HeaderStat label="Balance" value={summary?.balance} icon="ri-file-add-line" gradient="bg-primary-gradient" />
                <HeaderStat label="Equity" value={summary?.equity} icon="ri-group-line" gradient="bg-info" />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeaderStat({ label, value, icon, gradient }: { label: string; value: number | null | undefined; icon: string; gradient: string }) {
  return (
    <div className="d-flex align-items-center">
      <span className={`avatar avatar-md ${gradient} rounded-3 me-2 d-inline-flex align-items-center justify-content-center`}>
        <i className={`${icon} fs-18 text-white`} />
      </span>
      <div>
        <p className="mb-0 text-muted fs-13">{label}</p>
        <h5 className="mb-0 fw-semibold">{fmt(value)}</h5>
      </div>
    </div>
  );
}

function HistoryFilters({
  draft,
  setDraft,
  onFetch,
  onToggleAll,
  onToggleDeal,
  loading,
}: {
  draft: HistState;
  setDraft: React.Dispatch<React.SetStateAction<HistState>>;
  onFetch: () => void;
  onToggleAll: (c: boolean) => void;
  onToggleDeal: (key: DealKey, c: boolean) => void;
  loading: boolean;
}) {
  return (
    <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
      <div style={{ minWidth: 230 }}>
        <label className="form-label">Date Range</label>
        <DateRangePicker
          mode="range"
          value={draft.begDate && draft.endDate ? { start: draft.begDate, end: draft.endDate } : null}
          onChange={(v) => setDraft((d) => ({ ...d, begDate: v.start, endDate: v.end, allHistory: false }))}
        />
      </div>
      <Check label="All History" checked={draft.allHistory} onChange={onToggleAll} />
      {DEAL_TYPES.map((t) => (
        <Check key={t.key} label={t.label} checked={draft[t.key]} onChange={(c) => onToggleDeal(t.key, c)} />
      ))}
      <button type="button" className="btn btn-primary btn-sm" onClick={onFetch} disabled={loading}>
        {loading ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-search me-1" />}Fetch
      </button>
    </div>
  );
}

function Check({ label, checked, onChange }: { label: string; checked: boolean; onChange: (c: boolean) => void }) {
  const id = useId();
  return (
    <div className="form-check mb-1">
      <input id={id} className="form-check-input" type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <label className="form-check-label" htmlFor={id}>{label}</label>
    </div>
  );
}

const NOP_COLUMNS: DataGridColumn<NetOpenPositionDto>[] = [
  { key: 'symbol', title: 'Symbol', className: 'text-start', value: (r) => r.symbol },
  { key: 'netVolume', title: 'Net Volume', className: 'text-end', value: (r) => r.netVolume, cell: (r) => formatNumber(r.netVolume, 2) },
  { key: 'openPrice', title: 'Open Price', className: 'text-end', value: (r) => r.openPrice, cell: (r) => formatNumber(r.openPrice, r.digits || 5) },
  { key: 'floatingPnL', title: 'Floating PnL', className: 'text-end', value: (r) => r.floatingPnL, cell: (r) => <span className={pnlClass(r.floatingPnL)}>{formatNumber(r.floatingPnL)}</span> },
  { key: 'bid', title: 'Last Price', className: 'text-end', value: (r) => r.bid, cell: (r) => formatNumber(r.bid, r.digits || 5) },
  { key: 'connector', title: 'Connector', className: 'text-start', value: (r) => r.connector },
  { key: 'concentration', title: 'Concentration (%)', className: 'text-end', value: (r) => r.concentration, cell: (r) => formatNumber(r.concentration, 2) },
];

const OV_COLUMNS: DataGridColumn<OverViewTableRecord>[] = [
  { key: 'ticket', title: 'Ticket', className: 'text-start', value: (r) => r.ticket },
  { key: 'time', title: 'Time', className: 'text-start', value: (r) => r.time, cell: (r) => formatDateTime(r.time) },
  { key: 'symbol', title: 'Symbol', className: 'text-start', value: (r) => r.symbol },
  { key: 'type', title: 'Type', className: 'text-start', value: (r) => r.type },
  { key: 'volume', title: 'Volume', className: 'text-end', value: (r) => r.volume, cell: (r) => formatNumber(r.volume, 2) },
  { key: 'price', title: 'Price', className: 'text-end', value: (r) => r.price, cell: (r) => formatNumber(r.price, r.digits || 5) },
  { key: 'sl', title: 'SL', className: 'text-end', value: (r) => r.sl, cell: (r) => formatNumber(r.sl, 5) },
  { key: 'tp', title: 'TP', className: 'text-end', value: (r) => r.tp, cell: (r) => formatNumber(r.tp, 5) },
  { key: 'reason', title: 'Reason', className: 'text-start', value: (r) => r.reason },
  { key: 'closedPrice', title: 'Closed Price', className: 'text-end', value: (r) => r.closedPrice, cell: (r) => formatNumber(r.closedPrice, r.digits || 5) },
  { key: 'commission', title: 'Commission', className: 'text-end', value: (r) => r.commission, cell: (r) => formatNumber(r.commission, 2) },
  { key: 'swap', title: 'Swap', className: 'text-end', value: (r) => r.swap, cell: (r) => formatNumber(r.swap, 2) },
  { key: 'profit', title: 'Unrealized PnL', className: 'text-end', value: (r) => r.profit, cell: (r) => <span className={pnlClass(r.profit)}>{formatNumber(r.profit, 2)}</span> },
];

const HIST_COLUMNS: DataGridColumn<HistoryTableRecord>[] = [
  { key: 'time', title: 'Time', className: 'text-start', value: (r) => r.time, cell: (r) => formatDateTime(r.time) },
  { key: 'deal', title: 'Deal', className: 'text-start', value: (r) => r.deal },
  { key: 'position', title: 'Position', className: 'text-start', value: (r) => r.position ?? null },
  { key: 'symbol', title: 'Symbol', className: 'text-start', value: (r) => r.symbol },
  { key: 'type', title: 'Type', className: 'text-start', value: (r) => r.type },
  { key: 'entry', title: 'Entry', className: 'text-start', value: (r) => r.entry },
  { key: 'volume', title: 'Volume', className: 'text-end', value: (r) => r.volume ?? null, cell: (r) => (r.volume == null ? '' : formatNumber(r.volume, 2)) },
  { key: 'price', title: 'Price', className: 'text-end', value: (r) => r.price ?? null, cell: (r) => (r.price == null ? '' : formatNumber(r.price, r.digits || 5)) },
  { key: 'commission', title: 'Commission', className: 'text-end', value: (r) => r.commission ?? null, cell: (r) => (r.commission == null ? '' : formatNumber(r.commission, 2)) },
  { key: 'swap', title: 'Swap', className: 'text-end', value: (r) => r.swap ?? null, cell: (r) => (r.swap == null ? '' : formatNumber(r.swap, 2)) },
  { key: 'profit', title: 'Profit', className: 'text-end', value: (r) => r.profit, cell: (r) => <span className={pnlClass(r.profit)}>{formatNumber(r.profit, 2)}</span> },
  { key: 'comment', title: 'Comment', className: 'text-start', value: (r) => r.comment },
];
